import { THREE } from "@scrolly/renderer";
import { logError } from "../utils/logger";
import VideoMaterial from "./VideoMaterial";
import type { VideoContent } from "@scrolly/post-types";

const PLAY_DEBOUNCE = 100; // ms

// Legacy code typing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(THREE.VideoTexture.prototype as any).update = () => null; // we manually update

class Video {
    videoPlayer: HTMLVideoElement;
    videoMaterial: VideoMaterial | undefined;
    videoTexture: THREE.VideoTexture | undefined;
    isPlaying = false;
    hasEnded = false;
    cancelPlay: ReturnType<typeof setTimeout> | undefined;

    // content.muted aus dem Layer-JSON. Wird in generateTexture() gesetzt.
    // play() entstummt nur, wenn dieses Flag nicht gesetzt ist — sonst
    // werden Hintergrund-Videos mit content.muted=true bei jedem play()-
    // Call entstummt, und User hören mehrere überlappende Lifetime-
    // Videos gleichzeitig (Bene-Befund 2026-05-27 auf /scrollytelling/zuviel:
    // Wetterkarte-Background-Loop hatte content.muted=true im JSON,
    // wurde aber durch play() entstummt während Boris-Becker-Video lief).
    private contentMuted: boolean = false;

    // Ping-pong state: 1 = forward, -1 = reverse
    pingPongDirection: 1 | -1 = 1;
    private lastPingPongTime: number = 0;

    constructor() {
        this.generateTexture = this.generateTexture.bind(this);
        this.seek = this.seek.bind(this);

        const video = document.createElement("video");
        video.style.display = "none";
        video.addEventListener("play", () => {
            this.isPlaying = true;
            this.hasEnded = false;
        });
        video.addEventListener("ended", () => {
            this.hasEnded = true;
        });
        this.videoPlayer = video;
    }

    generateNormalVideo(props: VideoContent) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const videoTexture:any = new THREE.VideoTexture(this.videoPlayer);
        this.videoTexture = videoTexture;
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.format = THREE.RGBAFormat;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getThreeColorSpace: any = THREE;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const texture_:any = (videoTexture as THREE.VideoTexture as THREE.Texture)/*["colorSpace"] = getThreeColorSpace.SRGBColorSpace;*/
        texture_.colorSpace = getThreeColorSpace.SRGBColorSpace;

        const material = new THREE.MeshBasicMaterial({
            map: videoTexture,
            alphaTest: 0.01,
            transparent: true,
            depthWrite: false, // removes artifacts
            side: props.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
        });

        return {
            material,
            texture: videoTexture,
            height: this.videoPlayer.videoHeight,
            width: this.videoPlayer.videoWidth,
        };
    }

    generateTransparentVideo(props: VideoContent) {
        this.videoMaterial = new VideoMaterial(this.videoPlayer, {
            chromaKey: props.chromaKey,
            transparent: props.transparent,
            tolerance: props.tolerance,
        });

        return {
            texture: this.videoMaterial.texture,
            material: this.videoMaterial.material,
            height: this.videoPlayer.videoHeight,
            width: this.videoPlayer.videoWidth,
        };
    }

    generateTexture(props: VideoContent, url: string) {
        return new Promise<{
            material: THREE.ShaderMaterial | THREE.MeshBasicMaterial;
            texture: THREE.Texture;
            height: number;
            width: number;
        }>((resolve) => {
            this.videoPlayer.addEventListener("loadedmetadata", () => {
                const video = props.transparent
                    ? this.generateTransparentVideo(props)
                    : this.generateNormalVideo(props);
                resolve(video);
            });
            this.videoPlayer.crossOrigin = "anonymous";
            this.videoPlayer.preload = "auto";
            this.videoPlayer.src = url;
            this.videoPlayer.loop = props.loop;
            this.videoPlayer.muted = props.muted;
            this.contentMuted = !!props.muted;
            if (props.muted) {
                this.videoPlayer.setAttribute("muted", "");
            }

            this.videoPlayer.playsInline = true;
            this.videoPlayer.setAttribute("playsinline", "");
            this.videoPlayer.addEventListener("error", (err) => {
                const video = props.transparent
                    ? this.generateTransparentVideo(props)
                    : this.generateNormalVideo(props);
                resolve(video);
                logError(err as unknown as Error);
            });
            this.videoPlayer.load();

            this.videoPlayer.addEventListener("playing", (_event) => {
                console.log("Video is no longer paused");
            });
        });
    }

    playVirtual() {
        this.isPlaying = true;
    }

    pauseVirtual() {
        this.isPlaying = false;
    }

    delete() {
        this.videoPlayer.pause();
        // Clear src and force release of network/decode resources
        this.videoPlayer.removeAttribute("src");
        this.videoPlayer.load();
        this.videoPlayer.remove();
        // Dispose Three.js video texture (frees GPU memory)
        if (this.videoTexture) {
            this.videoTexture.dispose();
            this.videoTexture = undefined;
        }
        // Dispose video material if using chroma-key shader
        if (this.videoMaterial) {
            this.videoMaterial.material.dispose();
            this.videoMaterial.texture.dispose();
            this.videoMaterial = undefined;
        }
    }

    play() {
        this.isPlaying = true;
        clearTimeout(this.cancelPlay);
        const _play = async () => {
            try {
                await this.videoPlayer.play();
                this.videoPlayer.volume = 1.0;
                // Nur entstummen wenn content.muted=false war. Die unmute-
                // on-play existiert weil Browser-Auto-Play-Policy Videos
                // initial mutet — nach User-Klick darf entstummt werden.
                // Aber content.muted=true (Background-Loop) muss respektiert
                // werden, sonst Audio-Doppel bei überlappenden Lifetimes.
                if (!this.contentMuted) {
                    this.videoPlayer.muted = false;
                }
            } catch (e) {
                logError(e as Error);
            }
        };

        this.cancelPlay = setTimeout(_play, PLAY_DEBOUNCE);
    }

    pause() {
        clearTimeout(this.cancelPlay);
        this.videoPlayer.pause();
        this.isPlaying = false;
    }

    seek(timer: number) {
        clearTimeout(this.cancelPlay);
        this.videoPlayer.currentTime = timer;
    }

    seekToEnd() {
        clearTimeout(this.cancelPlay);
        this.videoPlayer.currentTime = this.videoPlayer.duration;
    }

    /**
     * Advance currentTime manually for ping-pong mode.
     * Video must be paused; we drive currentTime by deltaTime.
     */
    updatePingPong() {
        const now = performance.now();
        if (this.lastPingPongTime === 0) {
            this.lastPingPongTime = now;
            return;
        }
        const delta = (now - this.lastPingPongTime) / 1000; // seconds
        this.lastPingPongTime = now;

        const duration = this.videoPlayer.duration || 1;
        let newTime = this.videoPlayer.currentTime + this.pingPongDirection * delta;

        // Bounce at boundaries
        if (newTime >= duration) {
            newTime = duration - 0.01;
            this.pingPongDirection = -1;
        } else if (newTime <= 0) {
            newTime = 0;
            this.pingPongDirection = 1;
        }

        this.videoPlayer.currentTime = newTime;
    }

    resetPingPong() {
        this.pingPongDirection = 1;
        this.lastPingPongTime = 0;
    }
}

export default Video;
